import { useState, useEffect } from 'react';
import { useOrgTimezone, formatDateInTz, formatLocalDateInTz } from '../../utils/timezone';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ShoppingCart, Package, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp, Send } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import purchaseOrderService from '../../services/purchaseOrders';
import type { PurchaseOrder, POStatus } from '../../types/purchaseOrders';
import ReceiveStockModal from './ReceiveStockModal';
import PurchaseOrderReceipts from '../../components/purchaseOrders/PurchaseOrderReceipts';
import PurchaseOrderXeroCard from '../../components/purchaseOrders/PurchaseOrderXeroCard';
import { usePurchaseOrderAccess } from '../../hooks/usePurchaseOrderAccess';
import { toNum, toNumOr, type Numeric } from '@/utils/numeric';
import { money } from '@/utils/nurseryMoney';
import { ApiError } from '@/types/api';

/** The server's sentence (or the timeout's), else a fallback. */
function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.message) return err.message;
  const msg = (err as { response?: { data?: { error?: { message?: string } } } })
    .response?.data?.error?.message;
  return msg ?? fallback;
}

/** A unit cost to as many places as it was entered with (2–4): fuel is $2.593/L. */
function unitPrice(value: Numeric | undefined): string {
  const n = toNum(value);
  if (n === null) return '';
  return `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

const STATUS_LABELS: Record<POStatus, string> = {
  draft: 'Draft',
  ordered: 'Ordered',
  partially_received: 'Partially Received',
  received: 'Received',
  cancelled: 'Cancelled',
};

const STATUS_COLORS: Record<POStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  ordered: 'bg-blue-100 text-blue-700',
  partially_received: 'bg-amber-100 text-amber-700',
  received: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

function POStatusBadge({ status }: { status: POStatus }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

/**
 * One purchase order. Someone the organisation's admin has authorised sends,
 * orders, receives and cancels it; an Accounts seat reads it, to match a
 * supplier's invoice to what was ordered, for which farm and by whom.
 */
export default function PurchaseOrderDetail() {
  // Retail Staff receive deliveries without raising orders (retail plan Phase 1).
  const { canRaise: canAct, canReceive: canTakeDelivery, canEditSuppliers } = usePurchaseOrderAccess();
  const timezone = useOrgTimezone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [expandedReceipt, setExpandedReceipt] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // A refresh after an action that fails keeps the order on screen and says
  // so; only the first load replaces the page with an error.
  async function loadPO(refresh = false) {
    if (!id) return;
    if (!refresh) setLoading(true);
    try {
      const res = await purchaseOrderService.get(parseInt(id));
      if (res.success && res.data) {
        setPo(res.data);
        setError(null);
      } else if (!refresh) {
        setError('Purchase order not found');
      }
    } catch (err) {
      if (refresh) setActionError(errorText(err, 'The change was saved, but the page could not refresh. Reopen it to see the latest.'));
      else setError(errorText(err, 'Failed to load purchase order'));
    } finally {
      if (!refresh) setLoading(false);
    }
  }

  useEffect(() => { loadPO(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function markAsOrdered() {
    if (!po) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await purchaseOrderService.update(po.id, {
        status: 'ordered',
        ordered_date: new Date().toISOString().split('T')[0],
      });
      await loadPO(true);
    } catch (err) {
      setActionError(errorText(err, 'Could not mark the order as ordered.'));
    } finally {
      setActionLoading(false);
    }
  }

  async function sendToSupplier() {
    if (!po) return;
    setSending(true);
    setSendResult(null);
    try {
      const res = await purchaseOrderService.send(po.id);
      if (res.success && res.data) {
        setSendResult({ ok: true, message: `Purchase order sent to ${res.data.sent_to}` });
        // Sending can put it in Xero too; the Xero card reads the order again.
        await loadPO(true);
      } else {
        const msg = (res as { error?: { message?: string } }).error?.message;
        setSendResult({ ok: false, message: msg ?? 'Failed to send purchase order.' });
      }
    } catch (err) {
      setSendResult({ ok: false, message: errorText(err, 'Failed to send purchase order.') });
    } finally {
      setSending(false);
    }
  }

  async function cancelPO() {
    if (!po) return;
    if (!confirm('Are you sure you want to cancel this purchase order?')) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await purchaseOrderService.update(po.id, { status: 'cancelled' });
      await loadPO(true);
    } catch (err) {
      setActionError(errorText(err, 'Could not cancel the order.'));
    } finally {
      setActionLoading(false);
    }
  }
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-sm text-gray-400">Loading...</p>
      </div>
    );
  }

  if (error || !po) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-red-600">{error ?? 'Purchase order not found'}</p>
          <button onClick={() => navigate('/purchase-orders')} className="mt-2 text-sm text-indigo-600 hover:underline">
            Back to list
          </button>
        </div>
      </div>
    );
  }

  const canMarkOrdered = canAct && po.status === 'draft';
  const canReceive = canTakeDelivery && ['ordered', 'partially_received'].includes(po.status);
  const canCancel = canAct && !['received', 'cancelled'].includes(po.status);
  const canSend = canAct && po.status !== 'cancelled';
  // The supplier's PO address when it has one, else its contact address.
  const supplierEmail = po.supplier_po_email || po.supplier_email || null;
  const hasOutstanding = po.line_items.some(li => toNumOr(li.quantity_received) < toNumOr(li.quantity_ordered));
  const lineTotal = (li: PurchaseOrder['line_items'][number]) => {
    const cost = toNum(li.unit_cost);
    return cost == null ? null : toNumOr(li.quantity_ordered) * cost;
  };
  const orderTotal = po.line_items.reduce((sum, li) => sum + (lineTotal(li) ?? 0), 0);
  const uncosted = po.line_items.filter(li => lineTotal(li) == null).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <>
        <PageHeader
          title={po.reference_number ?? `PO #${po.id}`}
          subtitle={po.supplier_org_name ?? po.supplier_name ?? 'No supplier'}
          icon={<ShoppingCart className="h-6 w-6 text-white" />}
          actionButtons={<POStatusBadge status={po.status} />}
        />
        <div className="max-w-3xl mx-auto px-4 py-6">
          {/* Actions */}
          {(canMarkOrdered || canReceive || canCancel || canSend) && (
            <div className="flex flex-wrap items-center gap-3 mb-6">
              {canSend && (
                <button
                  onClick={sendToSupplier}
                  disabled={sending || actionLoading}
                  title={supplierEmail ? `Send to ${supplierEmail}` : 'No supplier email on file'}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  {sending ? 'Sending…' : 'Send to Supplier'}
                </button>
              )}
              {canMarkOrdered && (
                <button
                  onClick={markAsOrdered}
                  disabled={actionLoading}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  <CheckCircle className="h-4 w-4" />
                  Mark as Ordered
                </button>
              )}
              {canReceive && hasOutstanding && (
                <button
                  onClick={() => setShowReceiveModal(true)}
                  disabled={actionLoading}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  <Package className="h-4 w-4" />
                  Receive Stock
                </button>
              )}
              {canCancel && (
                <button
                  onClick={cancelPO}
                  disabled={actionLoading}
                  className="flex items-center gap-2 border border-red-300 text-red-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50 ml-auto"
                >
                  <XCircle className="h-4 w-4" />
                  Cancel Order
                </button>
              )}
            </div>
          )}

          {actionError && (
            <div className="flex items-center gap-2 mb-6 rounded-xl px-4 py-3 border bg-red-50 border-red-200">
              <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
              <p className="text-sm font-medium text-red-700 break-words min-w-0">{actionError}</p>
            </div>
          )}

          {sendResult && (
            <div className={`flex items-center gap-2 mb-6 rounded-xl px-4 py-3 border ${sendResult.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              {sendResult.ok
                ? <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
                : <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />}
              <p className={`text-sm font-medium ${sendResult.ok ? 'text-green-700' : 'text-red-700'}`}>{sendResult.message}</p>
            </div>
          )}
          {/* Details card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Order Details</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="col-span-2">
                <span className="text-gray-500">For:</span>
                <p className="font-medium text-gray-900 break-words">
                  {po.farm_name ? `${po.farm_name}${po.farm_code ? ` (${po.farm_code})` : ''}` : 'No farm recorded'}
                </p>
                {po.paid_by_card && (
                  <p className="mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800">
                    Paid on the company card
                  </p>
                )}
              </div>
              <div className="min-w-0">
                <span className="text-gray-500">Supplier:</span>
                <p className="font-medium text-gray-900 break-words">{po.supplier_org_name ?? po.supplier_name ?? '—'}</p>
                {po.supplier_email && <p className="text-gray-500 text-xs break-all">{po.supplier_email}</p>}
                {po.supplier_phone && <p className="text-gray-500 text-xs">{po.supplier_phone}</p>}
                {po.supplier_po_email && po.supplier_po_email !== po.supplier_email && (
                  <p className="text-gray-500 text-xs break-all mt-1">Orders go to {po.supplier_po_email}</p>
                )}
                {!supplierEmail && po.status !== 'cancelled' && (
                  <p className="text-amber-700 text-xs mt-1 break-words">
                    No email to send the order to.{' '}
                    {canEditSuppliers ? (
                      <Link
                        to={po.supplier_id ? `/accounts/suppliers?edit=${po.supplier_id}` : '/accounts/suppliers'}
                        className="underline"
                      >
                        {po.supplier_id ? 'Add one to this supplier' : 'Add the supplier'}
                      </Link>
                    ) : (
                      'An Accounts seat adds one under Accounts → Suppliers.'
                    )}
                  </p>
                )}
              </div>
              <div className="min-w-0">
                <span className="text-gray-500">Raised:</span>
                <p className="font-medium text-gray-900">{formatDateInTz(po.created_at, timezone)}</p>
                <p className="text-gray-400 text-xs break-words">by {po.creator_first_name} {po.creator_last_name}</p>
              </div>
              {po.ordered_date && (
                <div>
                  <span className="text-gray-500">Ordered:</span>
                  <p className="font-medium text-gray-900">{formatLocalDateInTz(po.ordered_date, timezone)}</p>
                </div>
              )}
              {po.expected_delivery_date && (
                <div>
                  <span className="text-gray-500">Expected delivery:</span>
                  <p className="font-medium text-gray-900">{formatLocalDateInTz(po.expected_delivery_date, timezone)}</p>
                </div>
              )}
            </div>
            {po.notes && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <span className="text-xs text-gray-500">Notes:</span>
                <p className="text-sm text-gray-700 mt-0.5">{po.notes}</p>
              </div>
            )}
          </div>

          {/* Line Items */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">
              Line Items ({po.line_items.length})
            </h2>
            {po.line_items.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No line items on this order</p>
            ) : (
              <div className="space-y-3">
                {po.line_items.map(item => {
                  const ordered = toNumOr(item.quantity_ordered);
                  const received = toNumOr(item.quantity_received);
                  const pct = ordered > 0
                    ? Math.min(100, Math.round((received / ordered) * 100))
                    : 0;
                  const isComplete = received >= ordered;
                  return (
                    <div key={item.id} className="border border-gray-100 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {item.item_type === 'chemical'
                              ? (item.product_name ?? 'Unknown chemical')
                              : item.item_description}
                          </p>
                          {item.item_type === 'chemical' && item.trade_name && (
                            <p className="text-xs text-gray-400">{item.trade_name}</p>
                          )}
                          {item.item_type === 'general' && (
                            <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">General</span>
                          )}
                          {item.item_type === 'nursery' && (
                            <span className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">
                              {item.stock_item_kind === 'merchandise' ? 'Stock · merchandise' : 'Stock · plant'}
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-gray-900">
                            {item.quantity_received} / {item.quantity_ordered} {item.unit}
                          </p>
                          {item.unit_cost && (
                            <p className="text-xs text-gray-400">{unitPrice(item.unit_cost)}/{item.unit}</p>
                          )}
                          {lineTotal(item) != null && (
                            <p className="text-xs text-gray-600">{money(lineTotal(item))}</p>
                          )}
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all ${isComplete ? 'bg-green-500' : 'bg-blue-500'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400 shrink-0">{pct}%</span>
                        {isComplete && <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {po.line_items.length > 0 && uncosted < po.line_items.length && (
              <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <span className="text-sm text-gray-500">
                  Order total{uncosted > 0 && ` (${uncosted} item${uncosted !== 1 ? 's' : ''} with no cost entered)`}
                </span>
                <span className="text-sm font-semibold text-gray-900">{money(orderTotal)}</span>
              </div>
            )}
          </div>
          <PurchaseOrderXeroCard po={po} />
          <PurchaseOrderReceipts poId={po.id} documents={po.documents ?? []} canAdd={canAct} onChanged={() => loadPO(true)} />

          {/* Stock received against the order's lines */}
          {po.receipts && po.receipts.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                Stock received ({po.receipts.length})
              </h2>
              <div className="space-y-2">
                {po.receipts.map(receipt => (
                  <div key={receipt.id} className="border border-gray-100 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedReceipt(expandedReceipt === receipt.id ? null : receipt.id)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <Package className="h-4 w-4 text-green-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Received {formatDateInTz(receipt.received_at, timezone)}
                          </p>
                          <p className="text-xs text-gray-400">
                            by {receipt.receiver_first_name} {receipt.receiver_last_name}
                            &nbsp;· {receipt.items.length} item{receipt.items.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      {expandedReceipt === receipt.id
                        ? <ChevronUp className="h-4 w-4 text-gray-400" />
                        : <ChevronDown className="h-4 w-4 text-gray-400" />
                      }
                    </button>
                    {expandedReceipt === receipt.id && (
                      <div className="px-4 pb-3 border-t border-gray-100">
                        {receipt.notes && <p className="text-xs text-gray-500 mt-2 mb-2">{receipt.notes}</p>}
                        <div className="space-y-1">
                          {receipt.items.map(ri => (
                            <div key={ri.id} className="flex items-center justify-between text-xs py-1">
                              <span className="text-gray-700 min-w-0 break-words">
                                {ri.product_name ?? ri.trade_name
                                  ?? po.line_items.find(li => li.id === ri.line_item_id)?.item_description ?? 'Item'}
                                {ri.batch_number && <span className="text-gray-400 ml-1">Batch: {ri.batch_number}</span>}
                              </span>
                              <div className="flex items-center gap-2 text-gray-500">
                                {(ri.location_name ?? ri.nursery_location_name) && (
                                  <span className="bg-gray-100 px-1.5 py-0.5 rounded">{ri.location_name ?? ri.nursery_location_name}</span>
                                )}
                                {ri.nursery_lot_id && (
                                  <Link to={`/nursery/lots/${ri.nursery_lot_id}`} className="text-indigo-600 hover:underline">Lot</Link>
                                )}
                                <span className="font-medium text-gray-900">{ri.quantity_received}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {po.status === 'received' && (
            <div className="mt-4 flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
              <p className="text-sm text-green-700 font-medium">All items have been received.</p>
            </div>
          )}

          {po.status === 'cancelled' && (
            <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
              <p className="text-sm text-red-700 font-medium">This purchase order has been cancelled.</p>
            </div>
          )}
        </div>
      </>

      {showReceiveModal && po && canTakeDelivery && (
        <ReceiveStockModal
          po={po}
          onClose={() => setShowReceiveModal(false)}
          onReceived={() => { setShowReceiveModal(false); loadPO(true); }}
        />
      )}
    </div>
  );
}
